import { formatDateTime } from '@/utils/formatDate';
import { useEffect, useState } from 'react';
import { Box, Card, CardContent, CardHeader, Chip, Typography } from '@mui/material';
import { apiGetById, apiPost } from '../../services/genesisApi/httpsMethods';
import { CustomMulitLineTextField } from '../../components/common/inputFields/customMultiLineTextFIeld';
import { CustomButton } from '../../components/common/customButton';
import { ConfirmDialog } from '../../components/common/confirmDialog';
import { useGlobalProvider } from '../../context/GlobalContext';
import { useCurrentUser } from '../../context/CurrentUserContext';
import { CustomSingleSelect } from '../../components/common/inputFields/customSingleSelect';

interface SponsorNote {
  id: number;
  note: string;
  note_type?: string | null;
  created_at: string;
  created_by_name?: string;
}

interface SponsorNotesCardsProps {
  sponsorId: number;
}

const NOTE_TYPES = ['Meeting', 'Info', 'General'];

export const SponsorNotesCards = ({ sponsorId }: SponsorNotesCardsProps) => {
  const { user, userLoading } = useGlobalProvider();
  const { canEdit } = useCurrentUser();

  const [notes, setNotes] = useState<SponsorNote[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [newNoteText, setNewNoteText] = useState<string>('');
  const [newNoteType, setNewNoteType] = useState<string>('General');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState<SponsorNote | null>(null);

  const hasValidSponsor = sponsorId != null && sponsorId > 0;

  const fetchNotes = async () => {
    if (!hasValidSponsor) return;
    setLoading(true);
    try {
      const response = await apiGetById('sponsor-notes', sponsorId);
      if (response && response.success && response.data) {
        setNotes(response.data as SponsorNote[]);
      } else {
        setNotes([]);
      }
    } catch (error) {
      console.error('Error fetching sponsor notes:', error);
      setNotes([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotes();
  }, [sponsorId]);

  const handleCreate = async () => {
    if (!hasValidSponsor) return;
    if (!newNoteText.trim() || userLoading || !user?.tenfiftyid) return;
    try {
      const payload: Record<string, unknown> = {
        note: newNoteText,
        created_by_id: user.tenfiftyid,
      };
      if (newNoteType) {
        payload.note_type = newNoteType;
      }
      const response = await apiPost(`sponsor-notes/${sponsorId}`, payload);
      if (response?.success) {
        setNewNoteText('');
        await fetchNotes();
      }
    } catch (error) {
      console.error('Error creating sponsor note:', error);
    }
  };

  const handleDelete = (note: SponsorNote) => {
    setNoteToDelete(note);
    setDialogOpen(true);
  };

  const disableAdd =
    !canEdit || !hasValidSponsor || !newNoteText.trim() || userLoading || !user?.tenfiftyid;

  return (
    <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Card variant="outlined">
        <CardContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ maxWidth: 220 }}>
              <CustomSingleSelect
                value={newNoteType}
                onChange={(e) => setNewNoteType(e.target.value)}
                options={NOTE_TYPES}
                placeholder="Note type"
                minWidth="160px"
                isStringArray
                fullWidth
              />
            </Box>
            <CustomMulitLineTextField
              label="Note"
              value={newNoteText}
              onChange={(val) => setNewNoteText(String(val))}
              margin="dense"
              delay={0}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <CustomButton
                text="Add Note"
                handleButtonPress={handleCreate}
                isButtonDisabled={disableAdd}
                isLoading={loading}
                isLoadingText="Saving"
                solid
              />
            </Box>
          </Box>
        </CardContent>
      </Card>

      {notes.map((note) => (
        <Card key={note.id} variant="outlined">
          <CardHeader
            title={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {note.note_type && (
                  <Chip label={note.note_type} size="small" color="primary" variant="outlined" />
                )}
                {note.created_by_name && (
                  <Typography variant="body2" color="text.secondary">
                    {note.created_by_name}
                  </Typography>
                )}
              </Box>
            }
            subheader={
              <Typography variant="caption" color="text.secondary">
                {formatDateTime(note.created_at)}
              </Typography>
            }
            action={
              <CustomButton
                text="Delete"
                handleButtonPress={() => handleDelete(note)}
                isButtonDisabled={!canEdit}
                solid={false}
                isDeleteButton
              />
            }
          />
          <CardContent>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {note.note}
            </Typography>
          </CardContent>
        </Card>
      ))}

      <ConfirmDialog
        open={dialogOpen}
        endpoint="sponsor-notes"
        id={noteToDelete?.id ?? 0}
        title="Delete Sponsor Note"
        description="Are you sure you want to delete this sponsor note? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        onClose={() => setDialogOpen(false)}
        onSuccess={() => {
          setDialogOpen(false);
          setNoteToDelete(null);
          fetchNotes();
        }}
      />
    </Box>
  );
};

export default SponsorNotesCards;

